import React, { useEffect, useRef, useState } from 'react'
import { useResendPhoneVerificationCode } from '../../../hooks/apolloHooks/useResendPhoneVerificationCode'
import { InfoIconOutlined } from '../../svgs/ExpressonSVGs'
import Tooltip from '../Tooltip'

type SetMainPhoneProps = {
	// Phone input
	phoneValue: string
	phoneError: string | undefined
	isPhoneValid: boolean
	loading: boolean
	handlePhoneChange: (e: React.ChangeEvent<HTMLInputElement>) => void
	handlePhoneVerify: () => void
	// Verification code input (shown after phone number is submitted)
	phoneVerificationSent: boolean
	addedPhoneId: number | null
	codeValue: string
	setCodeValue: (value: string) => void
	verifyLoading: boolean
	handleCodeVerification: () => void
	// Error state
	submitError: string | null
	onEditPhone: () => void
	isPhoneInputDisabled: boolean
}

const SetMainPhone: React.FC<SetMainPhoneProps> = ({
	phoneValue,
	phoneError,
	isPhoneValid,
	loading,
	handlePhoneChange,
	handlePhoneVerify,
	phoneVerificationSent,
	addedPhoneId,
	codeValue,
	setCodeValue,
	verifyLoading,
	handleCodeVerification,
	submitError,
	onEditPhone,
	isPhoneInputDisabled,
}) => {
	const [cooldownSeconds, setCooldownSeconds] = useState(0)
	const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const phoneInputRef = useRef<HTMLInputElement>(null)
	const codeInputRef = useRef<HTMLInputElement>(null)
	const { resendCode } = useResendPhoneVerificationCode()

	const handleResend = async () => {
		if (!addedPhoneId || cooldownSeconds > 0) return
		try {
			await resendCode({ phoneNumberId: addedPhoneId })
		} catch (err) {
			console.error('[SetMainPhone] resendCode failed:', err)
		}
		// Start 30-second cooldown regardless of success/failure
		setCooldownSeconds(30)
		cooldownRef.current = setInterval(() => {
			setCooldownSeconds(prev => {
				if (prev <= 1) {
					clearInterval(cooldownRef.current!)
					return 0
				}
				return prev - 1
			})
		}, 1000)
	}

	useEffect(() => {
		return () => { if (cooldownRef.current) clearInterval(cooldownRef.current) }
	}, [])

	// Auto-focus code input when it appears
	useEffect(() => {
		if (phoneVerificationSent) {
			setTimeout(() => codeInputRef.current?.focus(), 0)
		}
	}, [phoneVerificationSent])
	return (
		<div className="add-phone">
			<div className="input-wrapper">
				<label htmlFor="phone">Mobilnummer</label>
				<input
					ref={phoneInputRef}
					type="tel"
					id="phone"
					name="phone"
					placeholder="+467..."
					value={phoneValue}
					disabled={isPhoneInputDisabled}
					onChange={(e) => {
						const raw = e.target.value
						// Auto-replace leading 0 with +46
						const corrected = raw === '0' ? '+46' : raw
						handlePhoneChange({ ...e, target: { ...e.target, value: corrected } })
					}}
				/>
				<span>Vi skickar en verifieringskod till detta nummer.</span>
			</div>

			{phoneVerificationSent && (
				<div className="input-wrapper verification">
					<label htmlFor="phoneVerificationCode">
						Verifieringskod
						<Tooltip position="top-end" text="Verifieringskoden är giltig i 10 dagar. Om du inte har fått någon kod, kontrollera telefonnumret och klicka på 'Skicka verifieringskod igen'." >
                            <InfoIconOutlined width={25} height={26} />
                        </Tooltip>
					</label>
					<input
						type="text"
						inputMode="numeric"
						ref={codeInputRef}
						pattern="[0-9]*"
						id="phoneVerificationCode"
						name="phoneVerificationCode"
						placeholder="Ange verifieringskoden"
						maxLength={4}
						value={codeValue}
						onChange={(e) => setCodeValue(e.target.value.replace(/\D/g, ''))}
					/>
					<span>Fyll i koden du fick i SMS:et för att verifiera ditt telefonnummer.</span>
					<span
						className={`resend-verification${cooldownSeconds > 0 ? ' resend-verification--cooldown' : ''}`}
						onClick={handleResend}
					>
						{cooldownSeconds > 0
							? `Skicka igen om ${cooldownSeconds}s`
							: 'Skicka verifieringskod igen'
						}
					</span>
				</div>
			)}

			{phoneError && phoneValue.length > 0 && (
				<span className="input-error">{phoneError}</span>
			)}
			{submitError && (
				<span className="input-error">{submitError}</span>
			)}

			<div className="button-wrapper">
				{!phoneVerificationSent ? (
					<>
						<button
							type="button"
							className="button-edit body-text"
							disabled={!isPhoneValid || loading}
							onClick={handlePhoneVerify}
						>
							{loading ? 'Skickar...' : 'Skicka verifieringskod'}
						</button>
						{isPhoneInputDisabled && (
							<button
								type="button"
								className="button-edit body-text"
								onClick={() => {
									onEditPhone()
									setTimeout(() => phoneInputRef.current?.focus(), 0)
								}}
							>
								Redigera mobilnummer
							</button>
						)}
					</>
				) : (
					<>
						<button
							type="button"
							className="button-edit body-text"
							disabled={codeValue.length !== 4 || verifyLoading}
							onClick={handleCodeVerification}
						>
							{verifyLoading ? 'Verifierar...' : 'Verifiera mobilnummer'}
						</button>
						<button
							type="button"
							className="button-edit body-text"
							disabled={codeValue.length === 4}
							onClick={() => {
								onEditPhone()
								setTimeout(() => phoneInputRef.current?.focus(), 0)
							}}
						>
							Redigera mobilnummer
						</button>
					</>
				)}
			</div>
			
		</div>
	)
}

export default SetMainPhone